import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useApi } from '../../hooks/useApi';
import LoadingSpinner from '../../components/Common/LoadingSpinner';
import { ArrowLeftIcon, XMarkIcon } from '@heroicons/react/24/outline';

const EventForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { get, post, put, loading } = useApi();
  
  const [formData, setFormData] = useState({
    tripId: '',
    eventTypeId: '',
    timestamp: '',
    referenceNum: '',
    voiceText: ''
  });

  const [trips, setTrips] = useState([]);
  const [eventTypes, setEventTypes] = useState([]);
  const [availableFiles, setAvailableFiles] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [loadingTrips, setLoadingTrips] = useState(true);
  const [loadingEventTypes, setLoadingEventTypes] = useState(true);
  const [loadingFiles, setLoadingFiles] = useState(true);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    fetchAllData();
  }, [id]);

  const fetchAllData = async () => {
    // Fetch trips
    setLoadingTrips(true);
    const tripsRes = await get('/api/trips/filter?size=100');
    if (tripsRes.data) {
      setTrips(tripsRes.data.content || []);
      console.log('Trips loaded:', tripsRes.data.content);
    }
    setLoadingTrips(false);

    // Fetch event types
    setLoadingEventTypes(true);
    const eventTypesRes = await get('/api/event-types');
    if (eventTypesRes.data) {
      setEventTypes(eventTypesRes.data);
      console.log('Event Types loaded:', eventTypesRes.data);
    }
    setLoadingEventTypes(false);

    // Fetch available files
    setLoadingFiles(true);
    const filesRes = await get('/api/files/filter?size=100');
    if (filesRes.data) {
      setAvailableFiles(filesRes.data.content || []);
      console.log('Files loaded:', filesRes.data.content);
    }
    setLoadingFiles(false);

    // Fetch event data if editing
    if (id) {
      const eventRes = await get(`/api/events/${id}`);
      if (eventRes.data) {
        setFormData({
          tripId: eventRes.data.tripId || '',
          eventTypeId: eventRes.data.eventType?.id || '',
          timestamp: eventRes.data.timestamp ? eventRes.data.timestamp.slice(0, 16) : '',
          referenceNum: eventRes.data.referenceNum || '',
          voiceText: eventRes.data.voiceText || ''
        });
        
        // Load existing file IDs
        if (eventRes.data.fileIds) {
          setSelectedFiles(eventRes.data.fileIds);
        } else if (eventRes.data.files) {
          setSelectedFiles(eventRes.data.files.map(f => f.id));
        }
      }
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.tripId) {
      newErrors.tripId = 'Trip is required';
    }
    if (!formData.eventTypeId) {
      newErrors.eventTypeId = 'Event type is required';
    }
    if (!formData.timestamp) {
      newErrors.timestamp = 'Timestamp is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const handleFileToggle = (fileId) => {
    setSelectedFiles(prev => 
      prev.includes(fileId) 
        ? prev.filter(id => id !== fileId)
        : [...prev, fileId]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    const submitData = {
      tripId: parseInt(formData.tripId),
      eventTypeId: parseInt(formData.eventTypeId),
      timestamp: formData.timestamp,
      referenceNum: formData.referenceNum || null,
      voiceText: formData.voiceText || null,
      fileIds: selectedFiles  // ← Multiple files as array
    };

    console.log('Submitting event data:', submitData);

    let response;
    if (id) {
      response = await put(`/api/events/${id}`, submitData);
    } else {
      response = await post('/api/events', submitData);
    }

    if (!response.error) {
      navigate('/events/filter');
    }
  };

  if ((loading && id) || loadingTrips || loadingEventTypes || loadingFiles) return <LoadingSpinner />;

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="flex items-center mb-6">
        <button
          onClick={() => navigate('/events/filter')}
          className="mr-4 text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200"
        >
          <ArrowLeftIcon className="h-6 w-6" />
        </button>
        <h1 className="text-2xl font-bold text-gray-800 dark:text-white">
          {id ? 'Edit Event' : 'Create New Event'}
        </h1>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Trip Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Trip <span className="text-red-500">*</span>
              </label>
              <select
                name="tripId"
                value={formData.tripId}
                onChange={handleChange}
                className={`input-field ${errors.tripId ? 'border-red-500' : ''}`}
              >
                <option value="">Select a trip</option>
                {trips.map(trip => (
                  <option key={trip.id} value={trip.id}>
                    Trip #{trip.id} - {trip.label || 'No label'} 
                    {trip.driver && ` (${trip.driver.fullName})`}
                  </option>
                ))}
              </select>
              {errors.tripId && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.tripId}</p>
              )}
              {trips.length === 0 && (
                <p className="mt-1 text-sm text-yellow-600 dark:text-yellow-400">No trips found. Please create a trip first.</p>
              )}
            </div>

            {/* Event Type Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Event Type <span className="text-red-500">*</span>
              </label>
              <select
                name="eventTypeId"
                value={formData.eventTypeId}
                onChange={handleChange}
                className={`input-field ${errors.eventTypeId ? 'border-red-500' : ''}`}
              >
                <option value="">Select event type</option>
                {eventTypes.map(type => (
                  <option key={type.id} value={type.id}>
                    {type.description}
                  </option>
                ))}
              </select>
              {errors.eventTypeId && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.eventTypeId}</p>
              )}
              {eventTypes.length === 0 && (
                <p className="mt-1 text-sm text-yellow-600 dark:text-yellow-400">No event types found. Please create an event type first.</p>
              )}
            </div>

            {/* Timestamp */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Timestamp <span className="text-red-500">*</span>
              </label>
              <input
                type="datetime-local"
                name="timestamp"
                value={formData.timestamp}
                onChange={handleChange}
                className={`input-field ${errors.timestamp ? 'border-red-500' : ''}`}
              />
              {errors.timestamp && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.timestamp}</p>
              )}
            </div>

            {/* Reference Number */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Reference Number
              </label>
              <input
                type="text"
                name="referenceNum"
                value={formData.referenceNum}
                onChange={handleChange}
                className="input-field"
                placeholder="Enter reference number"
              />
            </div>

            {/* Voice Text */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Voice Text
              </label>
              <textarea
                name="voiceText"
                value={formData.voiceText}
                onChange={handleChange}
                rows="4"
                className="input-field"
                placeholder="Enter voice text or description"
              />
            </div>

            {/* Files Selection - Multiple */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Associated Files
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 max-h-48 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg p-3 bg-gray-50 dark:bg-gray-700/50">
                {availableFiles.length > 0 ? (
                  availableFiles.map(file => (
                    <label
                      key={file.id}
                      className={`flex items-center space-x-2 p-2 border rounded-lg cursor-pointer transition-colors ${
                        selectedFiles.includes(file.id)
                          ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/30'
                          : 'border-gray-200 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-600'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedFiles.includes(file.id)}
                        onChange={() => handleFileToggle(file.id)}
                        className="h-4 w-4 text-indigo-600 rounded"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-700 dark:text-gray-300 truncate">
                          {file.path?.split('/').pop() || `File #${file.id}`}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {file.fileSize ? `${(file.fileSize / 1024).toFixed(1)} KB` : 'Unknown size'}
                          {file.isVideo && <span className="ml-2 text-indigo-600">🎬</span>}
                        </p>
                      </div>
                    </label>
                  ))
                ) : (
                  <p className="text-sm text-gray-500 dark:text-gray-400 col-span-3 text-center py-4">
                    No files available. Please upload files first.
                  </p>
                )}
              </div>
              {selectedFiles.length > 0 && (
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                  Selected {selectedFiles.length} file{selectedFiles.length > 1 ? 's' : ''}
                </p>
              )}
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end space-x-4 pt-4 border-t border-gray-200 dark:border-gray-700">
            <button
              type="button"
              onClick={() => navigate('/events/filter')}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
            >
              {loading ? 'Saving...' : id ? 'Update Event' : 'Create Event'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EventForm;